import React, { useState, useEffect, useMemo } from 'react';
import { getImageUrl } from '../utils/api';
import CampaignCountdown from './CampaignCountdown';

const CampaignDetailModal = ({ campaign, isOpen, onClose, onProductClick }) => {
    const [imageError, setImageError] = useState(false);
    const [currentRuleIndex, setCurrentRuleIndex] = useState(0);

    // Get products safely (use empty array if campaign is null)
    const products = useMemo(() => {
        return campaign?.products || [];
    }, [campaign?.products]);
    
    const campaignImage = campaign?.campaign_image || campaign?.media_url;
    
    // For quantity_discount, get all valid rules with product names
    const allQuantityRules = useMemo(() => {
        if (!campaign || campaign.campaign_type !== 'quantity_discount' || !campaign.quantity_rules || campaign.quantity_rules.length === 0) {
            return [];
        }
        
        const rules = campaign.quantity_rules
            .filter(rule => 
                rule.product_id && 
                (rule.unit_price || rule.total_price || (rule.buy_quantity && rule.get_quantity))
            )
            .map(rule => {
                const product = products.find(p => p.id === rule.product_id);
                if (!product || !product.name) {
                    return null;
                }
                return {
                    ...rule,
                    productName: product.name
                };
            })
            .filter(Boolean);
        
        return rules;
    }, [campaign?.campaign_type, campaign?.quantity_rules, products]);
    
    // Rotate through rules every 2.5 seconds
    useEffect(() => {
        if (allQuantityRules.length <= 1) return;
        
        const interval = setInterval(() => {
            setCurrentRuleIndex((prev) => (prev + 1) % allQuantityRules.length);
        }, 2500);
        
        return () => clearInterval(interval);
    }, [allQuantityRules.length]);

    // Early return AFTER all hooks
    if (!isOpen || !campaign) return null;

    // Format discount info
    const getDiscountText = () => {
        if (campaign.campaign_type === 'discount') {
            // For discount campaigns, show old price crossed out and new price (like combination campaigns)
            const product = products?.[0];
            if (product) {
                const originalPrice = parseFloat(product.price) || 0;
                let discountedPrice = originalPrice;
                
                // Calculate discounted price from campaign
                if (campaign.discount_type === 'percentage') {
                    const discountValue = parseFloat(campaign.discount_value) || 0;
                    discountedPrice = originalPrice * (1 - discountValue / 100);
                } else if (campaign.discount_type === 'fixed_amount') {
                    const discountValue = parseFloat(campaign.discount_value) || 0;
                    discountedPrice = Math.max(0, originalPrice - discountValue);
                }
                
                // Also check product's own discount_price if available
                if (product.discount_price) {
                    discountedPrice = parseFloat(product.discount_price);
                }
                
                if (originalPrice > discountedPrice && discountedPrice > 0) {
                    return (
                        <span className="flex items-center gap-2">
                            <span 
                                className="px-2.5 py-1 text-sm font-bold rounded-full line-through"
                                style={{
                                    backgroundColor: '#fee2e2',
                                    color: '#dc2626',
                                    border: '1px solid #dc2626'
                                }}
                            >
                                {originalPrice.toFixed(0)}₺
                            </span>
                            <span 
                                className="px-2.5 py-1 text-base font-bold rounded-full"
                                style={{
                                    backgroundColor: '#16a34a',
                                    color: '#ffffff'
                                }}
                            >
                                {discountedPrice.toFixed(0)}₺
                            </span>
                        </span>
                    );
                }
            }
            
            // Fallback to text if no product or no discount
            if (campaign.discount_type === 'percentage') {
                const value = parseFloat(campaign.discount_value);
                return `%${Math.round(value)} İndirim`;
            }
            return `${parseFloat(campaign.discount_value).toFixed(0)}₺ İndirim`;
        }
        if (campaign.campaign_type === 'combo' || campaign.campaign_type === 'combination') {
            const comboPrice = parseFloat(campaign.combo_price) || 0;
            
            // Calculate total original price from products
            let totalOriginalPrice = 0;
            if (products && products.length > 0) {
                if (campaign.combination_rules && Array.isArray(campaign.combination_rules)) {
                    // Use combination_rules for accurate calculation
                    totalOriginalPrice = campaign.combination_rules.reduce((sum, rule) => {
                        const ruleProduct = products.find(p => p.id === rule.product_id);
                        if (ruleProduct) {
                            const productPrice = parseFloat(ruleProduct.price) || 0;
                            const quantity = rule.min_quantity || 1;
                            return sum + (productPrice * quantity);
                        }
                        return sum;
                    }, 0);
                } else {
                    // Fallback: sum all product prices
                    totalOriginalPrice = products.reduce((sum, product) => {
                        const productPrice = parseFloat(product.price) || 0;
                        const quantity = product.quantity || 1;
                        return sum + (productPrice * quantity);
                    }, 0);
                }
            }
            
            // If we have total original price and it's greater than combo price, show both
            if (totalOriginalPrice > 0 && totalOriginalPrice > comboPrice && comboPrice > 0) {
                return (
                    <span className="flex items-center gap-2">
                        <span 
                            className="px-2.5 py-1 text-sm font-bold rounded-full line-through"
                            style={{
                                backgroundColor: '#fee2e2',
                                color: '#dc2626',
                                border: '1px solid #dc2626'
                            }}
                        >
                            {totalOriginalPrice.toFixed(0)}₺
                        </span>
                        <span 
                            className="px-2.5 py-1 text-base font-bold rounded-full"
                            style={{
                                backgroundColor: '#16a34a',
                                color: '#ffffff'
                            }}
                        >
                            {comboPrice.toFixed(0)}₺
                        </span>
                    </span>
                );
            }
            
            return comboPrice > 0 ? `${comboPrice.toFixed(0)}₺` : 'Kampanya';
        }
        if (campaign.campaign_type === 'quantity_discount') {
            // Show rotating rules if available
            if (allQuantityRules.length > 0) {
                const currentRule = allQuantityRules[currentRuleIndex];
                const discountType = campaign.quantity_discount_type || 
                    (currentRule.unit_price ? 'unit_price' : 
                     currentRule.total_price ? 'total_price' : 
                     (currentRule.buy_quantity && currentRule.get_quantity) ? 'buy_x_get_y' : null);
                
                let totalPrice = null;
                let text = '';
                
                if (discountType === 'unit_price' && currentRule.unit_price) {
                    totalPrice = currentRule.min_quantity * parseFloat(currentRule.unit_price);
                    text = `${currentRule.min_quantity} Adet ${currentRule.productName} ${totalPrice.toFixed(0)} TL`;
                } else if (discountType === 'total_price' && currentRule.total_price) {
                    totalPrice = parseFloat(currentRule.total_price);
                    text = `${currentRule.min_quantity} Adet ${currentRule.productName} ${totalPrice.toFixed(0)} TL`;
                } else if (discountType === 'buy_x_get_y' && currentRule.buy_quantity && currentRule.get_quantity) {
                    if (currentRule.unit_price) {
                        totalPrice = currentRule.buy_quantity * parseFloat(currentRule.unit_price);
                        text = `${currentRule.buy_quantity}+${currentRule.get_quantity} ${currentRule.productName} ${totalPrice.toFixed(0)} TL`;
                    } else {
                        text = `${currentRule.buy_quantity}+${currentRule.get_quantity} ${currentRule.productName}`;
                    }
                }
                
                return text || 'Adet İndirimi';
            }
            return 'Adet İndirimi';
        }
        if (campaign.campaign_type === 'buy_x_get_y') {
            return `${campaign.buy_quantity}+${campaign.get_quantity}`;
        }
        return 'Kampanya';
    };

    return (
        <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
            onClick={onClose}
        >
            <div 
                className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-slideUp"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header / Image */}
                <div className="relative w-full aspect-video bg-gray-100 shrink-0">
                    {campaignImage && !imageError ? (
                        <img
                            src={getImageUrl(campaignImage)}
                            alt={campaign.title}
                            className="w-full h-full object-cover"
                            onError={() => setImageError(true)}
                        />
                    ) : products.length > 0 ? (
                        // For discount campaigns with single product, show product image full
                        campaign.campaign_type === 'discount' && products.length === 1 ? (
                            <div className="w-full h-full relative overflow-hidden bg-gray-200">
                                {(() => {
                                    const product = products[0];
                                    const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
                                    return primaryImage ? (
                                        <img
                                            src={getImageUrl(primaryImage.image_url)}
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-300">
                                            <span className="text-4xl opacity-50">🍽️</span>
                                        </div>
                                    );
                                })()}
                            </div>
                        ) : (
                            // Collage for multiple products or other campaign types (including quantity_discount)
                            <div className="w-full h-full grid grid-cols-2">
                                {products.slice(0, 4).map((product, idx) => {
                                    const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
                                    return (
                                        <div key={idx} className="relative overflow-hidden bg-gray-200">
                                            {primaryImage ? (
                                                <img
                                                    src={getImageUrl(primaryImage.image_url)}
                                                    alt={product.name}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-gray-300">
                                                    <span className="text-2xl opacity-50">🍽️</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                            <span className="text-4xl opacity-50">🎁</span>
                        </div>
                    )}

                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-white transition shadow-lg z-10"
                    >
                        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Discount Badge - Show for all campaigns except combination */}
                    {campaign.campaign_type !== 'combination' && campaign.campaign_type !== 'combo' && (
                        <div className="absolute bottom-4 right-4 bg-red-600 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">
                            {(() => {
                                const text = getDiscountText();
                                // If it's a React element (for discount campaigns with price display), render it
                                if (React.isValidElement(text)) {
                                    return text;
                                }
                                return typeof text === 'string' ? text : text;
                            })()}
                        </div>
                    )}

                    {/* Countdown */}
                    {campaign.end_date && (
                        <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                            <CampaignCountdown endDate={campaign.end_date} />
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{campaign.title}</h2>
                    
                    {campaign.description && (
                        <p className="text-gray-600 mb-4 leading-relaxed">{campaign.description}</p>
                    )}

                    {/* Campaign Type Badge and Product Count - Same as CampaignCard */}
                    <div className="flex items-center gap-2 mb-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {campaign.campaign_type === 'nox_choice' && '🏆 Sizin Seçiminiz'}
                            {campaign.campaign_type === 'discount' && '💰 İndirim'}
                            {campaign.campaign_type === 'combo' && '🎁 Kombinasyon'}
                            {campaign.campaign_type === 'combination' && '🎯 Kombinasyon'}
                            {campaign.campaign_type === 'quantity_discount' && '📦 Adet İndirimi'}
                            {campaign.campaign_type === 'buy_x_get_y' && '➕ Al Get'}
                            {campaign.campaign_type === 'cross_product' && '🔗 Çapraz Ürün'}
                            {campaign.campaign_type === 'cross_category' && '🔗 Çapraz Kategori'}
                        </span>
                        
                        {products && products.length > 0 && (
                            <span className="text-xs text-gray-500">
                                {products.length} ürün
                            </span>
                        )}
                    </div>

                    {/* Discount Campaign Price Display */}
                    {campaign.campaign_type === 'discount' && products.length > 0 && (() => {
                        const product = products[0];
                        const originalPrice = parseFloat(product.price) || 0;
                        let discountedPrice = originalPrice;
                        
                        // Calculate discounted price from campaign
                        if (campaign.discount_type === 'percentage') {
                            const discountValue = parseFloat(campaign.discount_value) || 0;
                            discountedPrice = originalPrice * (1 - discountValue / 100);
                        } else if (campaign.discount_type === 'fixed_amount') {
                            const discountValue = parseFloat(campaign.discount_value) || 0;
                            discountedPrice = Math.max(0, originalPrice - discountValue);
                        }
                        
                        // Also check product's own discount_price if available
                        if (product.discount_price) {
                            discountedPrice = parseFloat(product.discount_price);
                        }
                        
                        if (originalPrice > discountedPrice && discountedPrice > 0) {
                            return (
                                <div className="flex items-center gap-2 mb-4">
                                    <span 
                                        className="px-2.5 py-1 text-sm font-bold rounded-full line-through"
                                        style={{
                                            backgroundColor: '#fee2e2',
                                            color: '#dc2626',
                                            border: '1px solid #dc2626'
                                        }}
                                    >
                                        {originalPrice.toFixed(2)} ₺
                                    </span>
                                    <span 
                                        className="px-2.5 py-1 text-base font-bold rounded-full"
                                        style={{
                                            backgroundColor: '#16a34a',
                                            color: '#ffffff'
                                        }}
                                    >
                                        {discountedPrice.toFixed(2)} ₺
                                    </span>
                                </div>
                            );
                        }
                        return null;
                    })()}

                    {/* Combination Campaign Price Display */}
                    {(campaign.campaign_type === 'combination' || campaign.campaign_type === 'combo') && campaign.combo_price && (() => {
                        let totalOriginalPrice = 0;
                        if (campaign.combination_rules && Array.isArray(campaign.combination_rules) && products.length > 0) {
                            totalOriginalPrice = campaign.combination_rules.reduce((sum, rule) => {
                                const ruleProduct = products.find(p => p.id === rule.product_id);
                                if (ruleProduct) {
                                    const productPrice = parseFloat(ruleProduct.price) || 0;
                                    const quantity = rule.min_quantity || 1;
                                    return sum + (productPrice * quantity);
                                }
                                return sum;
                            }, 0);
                        } else if (products.length > 0) {
                            totalOriginalPrice = products.reduce((sum, product) => {
                                const productPrice = parseFloat(product.price) || 0;
                                const quantity = product.quantity || 1;
                                return sum + (productPrice * quantity);
                            }, 0);
                        }
                        
                        const comboPrice = parseFloat(campaign.combo_price) || 0;
                        
                        if (totalOriginalPrice > 0 && totalOriginalPrice > comboPrice && comboPrice > 0) {
                            return (
                                <div className="flex items-center gap-2 mb-4">
                                    {totalOriginalPrice && (
                                        <span 
                                            className="px-2.5 py-1 text-sm font-bold rounded-full line-through"
                                            style={{
                                                backgroundColor: '#fee2e2',
                                                color: '#dc2626',
                                                border: '1px solid #dc2626'
                                            }}
                                        >
                                            {totalOriginalPrice.toFixed(2)} ₺
                                        </span>
                                    )}
                                    <span 
                                        className="px-2.5 py-1 text-base font-bold rounded-full"
                                        style={{
                                            backgroundColor: '#16a34a',
                                            color: '#ffffff'
                                        }}
                                    >
                                        {comboPrice.toFixed(2)} ₺
                                    </span>
                                </div>
                            );
                        }
                        return null;
                    })()}

                    {/* Quantity Discount Details */}
                    {campaign.campaign_type === 'quantity_discount' && allQuantityRules.length > 0 && (
                        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Adet İndirim Detayları</h3>
                            <div className="space-y-2">
                                {allQuantityRules.map((rule, idx) => {
                                    const discountType = campaign.quantity_discount_type || 
                                        (rule.unit_price ? 'unit_price' : 
                                         rule.total_price ? 'total_price' : 
                                         (rule.buy_quantity && rule.get_quantity) ? 'buy_x_get_y' : null);
                                    
                                    let totalPrice = null;
                                    let originalPrice = null;
                                    let text = '';
                                    
                                    if (discountType === 'unit_price' && rule.unit_price) {
                                        totalPrice = rule.min_quantity * parseFloat(rule.unit_price);
                                        // Find product to get original price
                                        const product = products.find(p => p.id === rule.product_id);
                                        if (product) {
                                            originalPrice = rule.min_quantity * parseFloat(product.price || 0);
                                        }
                                        text = `${rule.min_quantity} Adet ${rule.productName}`;
                                    } else if (discountType === 'total_price' && rule.total_price) {
                                        totalPrice = parseFloat(rule.total_price);
                                        const product = products.find(p => p.id === rule.product_id);
                                        if (product) {
                                            originalPrice = rule.min_quantity * parseFloat(product.price || 0);
                                        }
                                        text = `${rule.min_quantity} Adet ${rule.productName}`;
                                    } else if (discountType === 'buy_x_get_y' && rule.buy_quantity && rule.get_quantity) {
                                        if (rule.unit_price) {
                                            totalPrice = rule.buy_quantity * parseFloat(rule.unit_price);
                                            const product = products.find(p => p.id === rule.product_id);
                                            if (product) {
                                                originalPrice = (rule.buy_quantity + rule.get_quantity) * parseFloat(product.price || 0);
                                            }
                                        }
                                        text = `${rule.buy_quantity}+${rule.get_quantity} ${rule.productName}`;
                                    }
                                    
                                    if (!text) return null;
                                    
                                    return (
                                        <div key={idx} className="p-3 bg-white rounded-lg border border-blue-100">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-semibold text-gray-900">{text}</span>
                                            </div>
                                            {originalPrice && totalPrice && originalPrice > totalPrice ? (
                                                <div className="flex items-center gap-2">
                                                    <span 
                                                        className="px-2 py-1 text-xs font-bold rounded-full line-through"
                                                        style={{
                                                            backgroundColor: '#fee2e2',
                                                            color: '#dc2626',
                                                            border: '1px solid #dc2626'
                                                        }}
                                                    >
                                                        {originalPrice.toFixed(2)} ₺
                                                    </span>
                                                    <span 
                                                        className="px-3 py-1 text-sm font-bold rounded-full"
                                                        style={{
                                                            backgroundColor: '#16a34a',
                                                            color: '#ffffff'
                                                        }}
                                                    >
                                                        {totalPrice.toFixed(2)} ₺
                                                    </span>
                                                </div>
                                            ) : totalPrice ? (
                                                <span 
                                                    className="px-3 py-1 text-sm font-bold rounded-full inline-block"
                                                    style={{
                                                        backgroundColor: '#16a34a',
                                                        color: '#ffffff'
                                                    }}
                                                >
                                                    {totalPrice.toFixed(2)} ₺
                                                </span>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Product Preview - Same as CampaignCard */}
                    {products && products.length > 0 && (
                        <div className="flex gap-1 flex-wrap mb-6">
                            {products.slice(0, 3).map((product) => (
                                <span
                                    key={product.id}
                                    className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                                    title={product.name}
                                >
                                    {product.name.substring(0, 15)}...
                                </span>
                            ))}
                            {products.length > 3 && (
                                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded font-semibold">
                                    +{products.length - 3}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Products List */}
                    {products.length > 0 && (
                        <div>
                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Kampanya Ürünleri</h3>
                            <div className="space-y-3">
                                {products.map((product) => (
                                    <div 
                                        key={product.id}
                                        onClick={() => onProductClick && onProductClick(product)}
                                        className="flex items-center gap-4 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition cursor-pointer border border-gray-100"
                                    >
                                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-200 shrink-0">
                                            {(() => {
                                                const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
                                                return primaryImage ? (
                                                    <img
                                                        src={getImageUrl(primaryImage.image_url)}
                                                        alt={product.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <span className="text-xl">🍽️</span>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-semibold text-gray-900 truncate">{product.name}</h4>
                                            <p className="text-sm text-gray-500 truncate">{product.description}</p>
                                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                {product.quantity && (
                                                    <span className="text-xs font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                                                        x{product.quantity}
                                                    </span>
                                                )}
                                                {/* Show old/new price for discount campaigns */}
                                                {campaign.campaign_type === 'discount' && product.discount_price ? (
                                                    <>
                                                        <span className="text-xs line-through font-medium text-gray-500">
                                                            {parseFloat(product.price).toFixed(2)} ₺
                                                        </span>
                                                        <span className="text-sm font-bold text-green-600">
                                                            {parseFloat(product.discount_price).toFixed(2)} ₺
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {parseFloat(product.price).toFixed(2)} ₺
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm text-gray-400">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px) scale(0.95); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                .animate-slideUp {
                    animation: slideUp 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default CampaignDetailModal;

